import React, { useState, useEffect } from 'react';
import { useMatterNumbering } from '../../../../hooks/useMatterNumbering';
import type { MatterNumberingTemplate, MatterNumberingConfig } from '../../../../../../types/matterNumbering';
import { 
  PencilIcon, 
  TrashIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { TemplateMatterService } from '../../../../../../services/templateMatterService';

interface TemplateManagementProps {
  config: MatterNumberingConfig;
  onConfigChange: (config: MatterNumberingConfig) => void;
}

interface TemplateFormData {
  name: string;
  description: string;
  isEnabled: boolean;
}

interface TemplateWithMatterCount extends MatterNumberingTemplate {
  matterCount: number;
  canEdit: boolean;
  canDelete: boolean;
}

export const TemplateManagement: React.FC<TemplateManagementProps> = ({
  config,
  onConfigChange
}) => {
  const [templates, setTemplates] = useState<TemplateWithMatterCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<MatterNumberingTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    isEnabled: true
  });
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

  const { getConfig, saveConfig } = useMatterNumbering();

  // Load templates with engagement counts.
  const loadTemplatesWithCounts = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseTemplates = config.customTemplates || [];
      const templateIds = baseTemplates.map(t => t.id);
      
      // Get engagement counts for all templates.
      const matterCounts = await TemplateMatterService.getTemplateMatterCounts(templateIds);
      
      // Enhance templates with engagement counts and edit permissions.
      const enhancedTemplates: TemplateWithMatterCount[] = baseTemplates.map(template => ({
        ...template,
        matterCount: matterCounts[template.id] || 0,
        canEdit: matterCounts[template.id] === 0,
        canDelete: matterCounts[template.id] === 0,
      }));

      setTemplates(enhancedTemplates);
    } catch (err) {
      console.error('Failed to load templates with engagement counts:', err);
      setError('Failed to load template usage data');
    } finally {
      setLoading(false);
    }
  };

  // Load templates when config changes
  useEffect(() => {
    loadTemplatesWithCounts();
  }, [config]);

  // Refresh templates
  const refreshTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const freshConfig = await getConfig();
      if (freshConfig) {
        onConfigChange(freshConfig);
      }
    } catch (err) {
      console.error('Failed to refresh templates:', err);
      setError('Failed to refresh templates');
    } finally {
      setLoading(false);
    }
  };

  // Update existing template, restricted based on engagement usage.
  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    try {
      setLoading(true);
      setError(null);

      const updatedTemplate: MatterNumberingTemplate = {
        ...editingTemplate,
        name: formData.name,
        description: formData.description,
        lastUpdatedBy: 'user',
        lastUpdatedDate: new Date().toISOString(),
        status: formData.isEnabled ? 'enabled' : 'disabled'
      };

      const updatedConfig = {
        ...config,
        customTemplates: config.customTemplates?.map(t => 
          t.id === editingTemplate.id ? updatedTemplate : t
        ) || []
      };

      await saveConfig(updatedConfig);
      onConfigChange(updatedConfig);
      setEditingTemplate(null);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        isEnabled: true
      });
      
    } catch (err) {
      console.error('Failed to update template:', err);
      setError('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  // Delete template with engagement-count check.
  const handleDeleteTemplate = async (template: TemplateWithMatterCount) => {
    try {
      setDeleteLoading(template.id);
      setDeleteMessage(null);

      // Check if template can be deleted
      const deleteCheck = await TemplateMatterService.canDeleteTemplate(template.id);
      
      if (!deleteCheck.canDelete) {
        setDeleteMessage(`Cannot Delete. There are ${deleteCheck.matterCount} active engagements using this template.`);
        setDeleteLoading(null);
        return;
      }

      // Confirm deletion
      if (!confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
        setDeleteLoading(null);
        return;
      }

      setDeleteMessage('Deleting template...');
      await TemplateMatterService.deleteTemplate(template.id);
      await refreshTemplates();
      
      setDeleteMessage('Template deleted successfully.');
      setTimeout(() => setDeleteMessage(null), 3000);
      
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError('Failed to delete template');
      setDeleteMessage(err instanceof Error ? err.message : 'Failed to delete template.');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Start editing template (restricted mode)
  const startEditTemplate = (template: MatterNumberingTemplate) => {
    setFormData({
      name: template.name,
      description: template.description,
      isEnabled: template.status === 'enabled'
    });
    setEditingTemplate(template);
  };

  // Cancel form
  const cancelForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      isEnabled: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Engagement Templates
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage custom engagement numbering templates. New templates are created in the Engagement Builder tab.
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={refreshTemplates}
            disabled={loading}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900/20 dark:border-red-600">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Delete Message Display */}
      {deleteMessage && (
        <div className={`border rounded-md p-4 ${
          deleteMessage.includes('Cannot Delete') || deleteMessage.includes('Failed')
            ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-600 dark:text-red-300'
            : deleteMessage.includes('Deleting')
            ? 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-600 dark:text-yellow-300'
            : 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-600 dark:text-green-300'
        }`}>
          <div className="flex items-center">
            {deleteMessage.includes('Cannot Delete') || deleteMessage.includes('Failed') ? (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            ) : deleteMessage.includes('successfully') ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
            )}
            <p>{deleteMessage}</p>
          </div>
        </div>
      )}

      {/* Edit Form (Restricted Mode) */}
      {editingTemplate && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 dark:bg-gray-800 dark:border-gray-600">
          <h4 className="text-md font-medium mb-4">
            Edit Template (Limited Mode)
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Template Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-lex-accent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Template name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-lex-accent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Template description"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isEnabled}
                onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable template
              </span>
            </label>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mt-4 dark:bg-yellow-900/20 dark:border-yellow-600">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                <p className="font-medium">Edit Restrictions:</p>
                <p>• Pattern cannot be modified once a template is created</p>
                <p>• Only name, description, and enable/disable status can be changed</p>
                <p>• To modify patterns, create a new template in the Engagement Builder tab</p>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2 mt-4">
            <button
              onClick={handleUpdateTemplate}
              disabled={!formData.name.trim() || loading}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Update'}
            </button>
            <button
              onClick={cancelForm}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white border border-gray-200 rounded-md dark:bg-gray-800 dark:border-gray-600">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
          <h4 className="text-md font-medium">
            Custom Templates ({templates.length})
          </h4>
        </div>
        
        {templates.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <p>No custom templates found.</p>
            <p className="text-sm mt-1">Create templates using the Engagement Builder tab.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagements Using</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pattern</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{template.name}</div>
                        {template.description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{template.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className={`font-semibold ${
                          template.matterCount > 0 
                            ? 'text-lex-accent dark:text-lex-accent' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {template.matterCount}
                        </span>
                        {template.matterCount > 0 && (
                          <span className="ml-2 text-xs bg-lex-accent-soft text-lex-accent px-2 py-1 rounded-full dark:bg-lex-accent-soft dark:text-lex-accent">
                            In Use
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm text-gray-900 dark:text-white">{template.pattern}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {template.examples?.[0]}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        template.status === 'enabled'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {template.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEditTemplate(template)}
                          className="p-1 text-lex-accent hover:bg-lex-state-hover rounded dark:hover:bg-lex-state-hover"
                          title="Edit (Name & Status Only)"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          disabled={deleteLoading === template.id}
                          className={`p-1 rounded ${
                            template.canDelete 
                              ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20' 
                              : 'text-gray-400 cursor-not-allowed'
                          }`}
                          title={template.canDelete ? 'Delete' : `Cannot delete - ${template.matterCount} engagements using`}
                        >
                          {deleteLoading === template.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}; 
